import { Helmet } from 'react-helmet-async';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { AlertTriangle, Phone, FileText, CheckCircle, Camera, MessageSquare, Shield } from 'lucide-react';
import WhatsAppButton from '@/components/WhatsAppButton';
const schadeStappen = [{
  icon: <Phone className="w-8 h-8 text-primary" />,
  title: "1. Meld direct",
  description: "Bel 078 – 220 00 21 tijdens kantooruren. Buiten kantooruren bel de alarmcentrale van uw verzekeraar.",
  urgent: true
}, {
  icon: <Camera className="w-8 h-8 text-primary" />,
  title: "2. Documenteer",
  description: "Maak foto's van de schade en bewaar alle relevante documenten en bonnetjes.",
  urgent: false
}, {
  icon: <FileText className="w-8 h-8 text-primary" />,
  title: "3. Wij regelen",
  description: "Wij nemen contact op met uw verzekeraar en begeleiden het gehele proces.",
  urgent: false
}, {
  icon: <CheckCircle className="w-8 h-8 text-primary" />,
  title: "4. Uitbetaling",
  description: "Na goedkeuring zorgen wij ervoor dat u snel uw schadevergoeding ontvangt.",
  urgent: false
}];
const schadeTypes = [{
  icon: <AlertTriangle className="w-12 h-12 text-orange-500" />,
  title: "Auto schade",
  description: "Aanrijding, diefstal, vandalisme of schade door weer",
  acties: ["Direct stoppen bij ongeval", "Politie bellen bij gewonden", "Foto's maken", "Ons direct bellen"]
}, {
  icon: <Shield className="w-12 h-12 text-blue-500" />,
  title: "Inboedel schade",
  description: "Diefstal, brand, waterschade of storm aan uw spullen",
  acties: ["Schade niet opruimen", "Foto's maken", "Lijst beschadigde spullen", "Bonnetjes bewaren"]
}, {
  icon: <MessageSquare className="w-12 h-12 text-green-500" />,
  title: "Woonhuis schade",
  description: "Schade aan uw woning door brand, storm of water",
  acties: ["Direct gevaar melden", "Foto's maken", "Tijdelijke reparaties", "Niet definitief repareren"]
}];
const noodgevallen = ["Zware verkeersongevallen met gewonden", "Brand in uw woning", "Ernstige waterschade", "Inbraak met veel schade", "Storm schade aan dak"];
const verzekeraarNoodNummers = [{
  naam: "Allianz",
  nummers: [{
    type: "Algemeen",
    nummer: "(088) 577 25 55"
  }]
}, {
  naam: "ASR Schadeverzekeringen N.V.",
  nummers: [{
    type: "Algemeen",
    nummer: "(030) 257 91 11"
  }, {
    type: "Binnenland",
    nummer: "(030) 256 77 77"
  }, {
    type: "Buitenland",
    nummer: "+31 30 256 77 77"
  }]
}, {
  naam: "De Goudse",
  nummers: [{
    type: "Algemeen",
    nummer: "(0182) 54 45 44"
  }, {
    type: "Binnenland",
    nummer: "(0182) 54 47 75"
  }, {
    type: "Buitenland",
    nummer: "(0182) 54 45 55"
  }]
}, {
  naam: "Klaverblad Verzekeringen",
  nummers: [{
    type: "Algemeen",
    nummer: "(079) 320 42 04"
  }, {
    type: "Buitenland",
    nummer: "0031 79 320 47 04"
  }]
}, {
  naam: "Nationale-Nederlanden Schade",
  nummers: [{
    type: "Algemeen",
    nummer: "026 400 23 90"
  }, {
    type: "Buitenland",
    nummer: "0031 26 400 23 90"
  }, {
    type: "Overlijden",
    nummer: "088 663 02 33"
  }]
}, {
  naam: "Nh1816 Verzekeringen",
  nummers: [{
    type: "Algemeen",
    nummer: "(0226) 33 18 16"
  }, {
    type: "Binnenland",
    nummer: "(088) 112 18 16"
  }, {
    type: "Buitenland",
    nummer: "+31 88 112 18 16"
  }]
}, {
  naam: "Noorderlinge verzekeraars",
  nummers: [{
    type: "Algemeen/Binnenland",
    nummer: "088 00 77098"
  }, {
    type: "Buitenland",
    nummer: "+3188 0077098"
  }]
}, {
  naam: "Turien & Co",
  nummers: [{
    type: "Algemeen/Binnenland",
    nummer: "072 5181181"
  }, {
    type: "Buitenland",
    nummer: "+3172 5181181"
  }]
}, {
  naam: "Voogd & Voogd",
  nummers: [{
    type: "Algemeen",
    nummer: "088 020 9100"
  }]
}];
const Schade = () => {
  return <div className="min-h-screen">
      <Helmet>
        <title>Schade melden | Direct hulp bij alle schades | Compleet Verzekerd</title>
        <meta name="description" content="Schade gehad? Bel 078-220 00 21 voor directe hulp. Wij regelen alles voor u: van schademelding tot uitbetaling. Snel, deskundig en persoonlijk." />
        <meta name="keywords" content="schade melden, schademelding, autoverzekering schade, inboedel schade, woonhuis schade, 078-220 00 21, verzekeraar noodnummers" />
        <link rel="canonical" href="https://compleetverzekerd.nl/schade/" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Schademelding Service",
            "provider": {
              "@type": "Organization",
              "name": "Compleet Verzekerd",
              "telephone": "0782200021"
            },
            "description": "Professionele hulp bij schademeldingen voor alle soorten verzekeringen",
            "serviceType": "Insurance Claims Service"
          })}
        </script>
      </Helmet>
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:rounded">
        Spring naar hoofdinhoud
      </a>
      
      <Header />
      
      <main id="main-content">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-red-50 to-orange-50 py-16 md:py-24">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <AlertTriangle className="w-20 h-20 text-orange-500 mx-auto mb-6" />
              <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6">
                Schade melden
              </h1>
              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                Je hebt schade?! Heel vervelend… Maar geen zorgen! Wij zijn er voor je.
                Wij wikkelen alles op een snelle, juiste en deskundige manier voor jou af.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 py-3" onClick={() => document.getElementById('noodnummers')?.scrollIntoView({
                behavior: 'smooth'
              })}>
                  {/* <Phone className="w-5 h-5" /> */}
                  Bekijk noodnummers
                </Button>
                <Button variant="outline" size="lg" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground" asChild>
                  <a href="tel:0782200021">
                    <Phone className="w-5 h-5" />
                    078 - 220 00 21
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Info Bar 
         <section className="py-4 bg-red-600">
          <div className="container mx-auto px-6">
            <div className="w-full h-1 bg-red-700 rounded"></div>
          </div>
         </section>
         */}

        {/* Schade Process */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Zo meldt u schade
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Stap voor stap door het schadeproces. Wij begeleiden u van melding tot uitbetaling.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {schadeStappen.map((stap, index) => <Card key={index} className={`border-border hover:shadow-lg transition-shadow ${stap.urgent ? 'ring-2 ring-red-500' : ''}`}>
                  <CardContent className="p-6 text-center">
                    <div className="mb-4 flex justify-center">{stap.icon}</div>
                    <h3 className="text-lg font-semibold text-foreground mb-3">{stap.title}</h3>
                    <p className="text-muted-foreground text-sm">
                      {stap.description.split('078 – 220 00 21').map((part, index) => (
                        index === 0 ? part : (
                          <>
                            <a href="tel:0782200021" className="text-primary hover:underline">078 – 220 00 21</a>
                            {part}
                          </>
                        )
                      ))}
                    </p>
                    {stap.urgent && <div className="mt-3 text-xs text-red-600 font-medium">URGENT</div>}
                  </CardContent>
                </Card>)}
            </div>
          </div>
        </section>

        {/* Schade Types */}
        <section className="py-16 bg-muted/50">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Veel voorkomende schades
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Per schade type hebben wij specifieke tips en acties voor u.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {schadeTypes.map((type, index) => <Card key={index} className="border-border hover:shadow-lg transition-shadow">
                  <CardHeader className="text-center">
                    <div className="mb-4 flex justify-center">{type.icon}</div>
                    <CardTitle className="text-xl text-foreground">{type.title}</CardTitle>
                    <p className="text-muted-foreground">{type.description}</p>
                  </CardHeader>
                  <CardContent>
                    <h4 className="font-semibold text-foreground mb-3">Belangrijke acties:</h4>
                    <ul className="space-y-2">
                      {type.acties.map((actie, idx) => <li key={idx} className="flex items-start">
                          <CheckCircle className="w-4 h-4 text-primary mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-muted-foreground">{actie}</span>
                        </li>)}
                    </ul>
                  </CardContent>
                </Card>)}
            </div>
          </div>
        </section>

        {/* Emergency Cases */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <AlertTriangle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                  Noodgevallen
                </h2>
                <p className="text-lg text-muted-foreground">Bij deze situaties belt u ons direct</p>
              </div>
              
              <Card className="border-orange-200 bg-orange-50/50">
                <CardContent className="p-8">
                  <ul className="grid md:grid-cols-2 gap-4">
                    {noodgevallen.map((noodgeval, index) => <li key={index} className="flex items-start">
                        <AlertTriangle className="w-5 h-5 text-orange-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground">{noodgeval}</span>
                      </li>)}
                  </ul>
                  
                  <div className="mt-8 text-center">
                    <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white" onClick={() => document.getElementById('noodnummers')?.scrollIntoView({
                    behavior: 'smooth'
                  })}>
                      Bekijk noodnummers
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Verzekeraar Noodnummers */}
        <section id="noodnummers" className="py-16 bg-muted/50">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <Phone className="w-16 h-16 text-primary mx-auto mb-4" />
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                  Verzekeraar Noodnummers
                </h2>
                <p className="text-lg text-muted-foreground">
                  Buiten onze kantooruren kunt u direct contact opnemen met de alarmcentrale van uw verzekeraar.
                </p>
              </div>

              <Card className="border-border">
                <CardContent className="p-6">
                  <Accordion type="single" collapsible className="w-full">
                    {verzekeraarNoodNummers.map((verzekeraar, index) => <AccordionItem key={index} value={`item-${index}`}>
                        <AccordionTrigger className="text-left hover:no-underline">
                          <div className="flex items-center">
                            <Shield className="w-5 h-5 text-primary mr-3" />
                            <span className="font-semibold">{verzekeraar.naam}</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="pt-4 pl-8">
                            <div className="grid gap-3">
                              {verzekeraar.nummers.map((nummer, nummerIndex) => <div key={nummerIndex} className="flex items-center justify-between border-b border-border/50 pb-2">
                                  <span className="text-sm font-medium text-muted-foreground">
                                    {nummer.type}
                                  </span>
                                  <div className="flex items-center">
                                    <Phone className="w-4 h-4 text-primary mr-2" />
                                    <a href={`tel:${nummer.nummer.replace(/[\s\(\)\-]/g, '')}`} className="text-primary font-mono hover:underline">
                                      {nummer.nummer}
                                    </a>
                                  </div>
                                </div>)}
                            </div>
                            <div className="mt-4 p-3 bg-muted rounded-md">
                              <p className="text-xs text-muted-foreground">
                                💡 <strong>Tip:</strong> Klik op het telefoonnummer om direct te bellen. 
                                Voor hulp bij het contact met uw verzekeraar kunt u ook altijd <a href="tel:0782200021" className="text-primary hover:underline">ons bellen op 078 – 220 00 21</a>.
                              </p>
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>)}
                  </Accordion>
                  
                  <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-start">
                      <Shield className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-blue-900 mb-2">
                          Hulp nodig bij het contact met uw verzekeraar?
                        </h4>
                        <p className="text-sm text-blue-800 mb-3">
                          Wij kennen de procedures van alle verzekeraars en kunnen u helpen bij complexe schademeldingen. 
                          Belt u ons gerust voor persoonlijke begeleiding.
                        </p>
                        <Button variant="outline" size="sm" className="border-blue-300 text-blue-700 hover:bg-blue-100" asChild>
                          <a href="tel:0782200021">
                            <Phone className="w-4 h-4" />
                            078 - 220 00 21
                          </a>
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-muted/50">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Veelgestelde vragen
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {[{
              question: "Wat moet ik doen na een schade?",
              answer: "Bel ons tijdens kantooruren op 078-220 00 21. Buiten kantooruren neemt u direct contact op met de alarmcentrale van uw verzekeraar."
            }, {
              question: "Hoe snel wordt mijn schade afgehandeld?",
              answer: "De meeste schades handelen wij binnen 48 uur af. Bij complexe schades kan dit iets langer duren, maar u wordt altijd op de hoogte gehouden."
            }, {
              question: "Wat moet ik doen na een auto-ongeluk?",
              answer: "Blijf kalm, zorg voor veiligheid, bel bij gewonden 112, maak foto's, noteer gegevens andere partij en vul het Europees schadeformulier in."
            }, {
              question: "Kan ik zelf een hersteller kiezen?",
              answer: "Bij veel verzekeraars kunt u zelf kiezen. Wij hebben ook een netwerk van betrouwbare herstelpartners."
            }].map((faq, index) => <Card key={index} className="border-border">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-foreground mb-3">{faq.question}</h3>
                    <p className="text-muted-foreground text-sm">
                      {faq.answer.split('078-220 00 21').map((part, index) => (
                        index === 0 ? part : (
                          <>
                            <a href="tel:0782200021" className="text-primary hover:underline">078-220 00 21</a>
                            {part}
                          </>
                        )
                      ))}
                    </p>
                  </CardContent>
                </Card>)}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 bg-primary text-primary-foreground">
          <div className="container mx-auto px-6 text-center">
            <Shield className="w-16 h-16 mx-auto mb-6 opacity-80" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Wij staan voor u klaar
            </h2>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              Tijdens kantooruren helpen wij u graag bij het afhandelen van uw schade. 
              Buiten kantooruren verwijzen wij u door naar uw verzekeraar.
            </p>
            <Button variant="secondary" size="lg" className="bg-background text-foreground hover:bg-background/90" asChild>
              <a href="tel:0782200021">
                <Phone className="w-5 h-5" />
                078 – 220 00 21
              </a>
            </Button>
          </div>
        </section>
      </main>
      
      <Footer />
      <WhatsAppButton />
    </div>;
};
export default Schade;